// hooks/useTaskClustering.ts - Updated with AI task support
import { useCallback, useEffect, useState } from 'react';
import { TaskWithAi, isAiTask } from '../types/ai-task';
import { getNearestNode } from '../utils/node';

export interface TaskCluster {
  id: string;
  element: Element | null;
  elementPath: string;
  tasks: TaskWithAi[];
  position: { top: number; left: number };
  isExpanded: boolean;
}

export interface ClusteredTaskData {
  clusters: TaskCluster[];
  individualTasks: TaskWithAi[];
}

/**
 * Hook to manage task clustering logic
 * Groups tasks (including AI tasks) that resolve to the same DOM element
 * Updated to work with floating cluster panels and AI tasks
 */
export const useTaskClustering = (tasks: TaskWithAi[]) => {
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(
    new Set()
  );
  const [animatedClusters, setAnimatedClusters] = useState<Set<string>>(
    new Set()
  );
  const [elementCache, setElementCache] = useState<Map<string, Element | null>>(
    new Map()
  );
  const [clusteredData, setClusteredData] = useState<ClusteredTaskData>({
    clusters: [],
    individualTasks: [],
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Helper function to get element with caching
  const getElementForPath = useCallback(
    async (path: string): Promise<Element | null> => {
      if (elementCache.has(path)) {
        return elementCache.get(path) || null;
      }

      try {
        const element = await getNearestNode(path);
        setElementCache((prev) => new Map(prev).set(path, element));
        return element;
      } catch (error) {
        console.error('Error finding element for clustering:', error);
        setElementCache((prev) => new Map(prev).set(path, null));
        return null;
      }
    },
    [elementCache]
  );

  // Check if element is still valid (not removed from DOM)
  const isElementValid = useCallback((element: Element | null): boolean => {
    if (!element) return false;
    return document.contains(element);
  }, []);

  // Calculate cluster position based on element
  const calculateClusterPosition = useCallback((element: Element) => {
    const rect = element.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;

    return {
      top: rect.top + scrollY + rect.height / 2,
      left: rect.left + scrollX + rect.width / 2,
    };
  }, []);

  // Helper to get element path from any task type
  const getTaskElementPath = useCallback((task: TaskWithAi): string | null => {
    // AI tasks
    if (isAiTask(task)) {
      return task.task_element_path || null;
    }

    // Regular tasks
    return task.task_element_path || null;
  }, []);

  // Helper to check if task should be excluded from clustering
  const shouldExcludeFromClustering = useCallback(
    (task: TaskWithAi): boolean => {
      // Exclude special tasks
      if (
        task.task_id.includes('PENDING') ||
        task.task_id.includes('OPTIMISTIC')
      ) {
        return true;
      }

      // Don't exclude AI tasks from clustering
      if (isAiTask(task)) {
        return false;
      }

      return false;
    },
    []
  );

  // Process tasks into clusters
  const processClustering = useCallback(async () => {
    if (isProcessing) return;
    // If there are no tasks, clear clustering state immediately to avoid stale clusters
    if (tasks.length === 0) {
      setClusteredData({ clusters: [], individualTasks: [] });
      return;
    }

    setIsProcessing(true);

    try {
      const elementGroups = new Map<Element, TaskWithAi[]>();
      const individualTasks: TaskWithAi[] = [];
      const clusters: TaskCluster[] = [];

      // First pass: group tasks by their resolved element
      const taskProcessingPromises = tasks.map(async (task) => {
        // Skip tasks that should be excluded from clustering
        if (shouldExcludeFromClustering(task)) {
          return { task, element: null, isIndividual: true };
        }

        const elementPath = getTaskElementPath(task);
        if (!elementPath) {
          return { task, element: null, isIndividual: true };
        }

        const element = await getElementForPath(elementPath);

        // Validate element is still in DOM
        const isValid = isElementValid(element);

        return {
          task,
          element: isValid ? element : null,
          isIndividual: !isValid || !element,
        };
      });

      const processedTasks = await Promise.all(taskProcessingPromises);

      // Group by element
      processedTasks.forEach(({ task, element, isIndividual }) => {
        if (isIndividual || !element) {
          individualTasks.push(task);
        } else {
          if (elementGroups.has(element)) {
            elementGroups.get(element)!.push(task);
          } else {
            elementGroups.set(element, [task]);
          }
        }
      });

      // Second pass: create clusters for groups with multiple tasks
      elementGroups.forEach((groupTasks, element) => {
        if (groupTasks.length > 1) {
          // Create cluster - sort regular tasks by comment_id, AI tasks by their original order
          const sortedTasks = groupTasks.sort((a, b) => {
            // If both are AI tasks, maintain original order
            if (
              'isAiTask' in a &&
              a.isAiTask &&
              'isAiTask' in b &&
              b.isAiTask
            ) {
              return 0; // Maintain original order
            }

            // If one is AI task and one is regular, put regular tasks first
            if ('isAiTask' in a && a.isAiTask) return 1;
            if ('isAiTask' in b && b.isAiTask) return -1;

            // Both are regular tasks, sort by comment_id
            return (a.task_comment_id || 0) - (b.task_comment_id || 0);
          });

          // Use element path for stable cluster ID - prefer regular task path
          const regularTask = sortedTasks.find(
            (t) => !('isAiTask' in t && t.isAiTask)
          );
          const elementPath =
            regularTask?.task_element_path ||
            getTaskElementPath(sortedTasks[0]);
          const clusterId = `cluster-${elementPath}`;

          // Calculate position for the cluster sticker
          const position = calculateClusterPosition(element);

          clusters.push({
            id: clusterId,
            element,
            elementPath: elementPath || '',
            tasks: sortedTasks,
            position,
            isExpanded: expandedClusters.has(clusterId),
          });
        } else {
          // Single task, keep as individual
          individualTasks.push(groupTasks[0]);
        }
      });

      setClusteredData({ clusters, individualTasks });
    } catch (error) {
      console.error('Error processing task clustering:', error);
      // Fallback: treat all tasks as individual
      setClusteredData({ clusters: [], individualTasks: tasks });
    } finally {
      setIsProcessing(false);
    }
  }, [
    tasks,
    expandedClusters,
    getElementForPath,
    isProcessing,
    isElementValid,
    calculateClusterPosition,
    shouldExcludeFromClustering,
    getTaskElementPath,
  ]);

  // Trigger clustering when tasks change
  useEffect(() => {
    processClustering();
  }, [tasks, processClustering]);

  // Prune caches and expansion/animation state when tasks or clusters change to prevent memory growth
  useEffect(() => {
    // When tasks change, prune element cache to only keep paths that are still relevant
    const relevantPaths = new Set(
      tasks
        .map((t) => getTaskElementPath(t))
        .filter((p): p is string => typeof p === 'string' && p.length > 0)
    );
    setElementCache((prev) => {
      if (prev.size === 0) return prev;
      const next = new Map<string, Element | null>();
      // Simple LRU-like prune: keep only relevant keys; if still too large, cap to first 200 entries
      for (const [key, value] of prev) {
        if (relevantPaths.has(key)) {
          next.set(key, value);
        }
      }
      if (next.size > 200) {
        const limited = new Map<string, Element | null>();
        let count = 0;
        for (const [key, value] of next) {
          limited.set(key, value);
          count += 1;
          if (count >= 200) break;
        }
        return limited;
      }
      return next;
    });
  }, [tasks, getTaskElementPath]);

  useEffect(() => {
    // When clusters change, ensure expanded/animated sets only contain valid cluster IDs
    const validIds = new Set(clusteredData.clusters.map((c) => c.id));
    setExpandedClusters((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      for (const id of prev) {
        if (validIds.has(id)) next.add(id);
      }
      return next;
    });
    setAnimatedClusters((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      for (const id of prev) {
        if (validIds.has(id)) next.add(id);
      }
      return next;
    });
  }, [clusteredData.clusters]);

  // Update cluster expansion states
  useEffect(() => {
    if (clusteredData.clusters.length > 0) {
      setClusteredData((prev) => ({
        ...prev,
        clusters: prev.clusters.map((cluster) => ({
          ...cluster,
          isExpanded: expandedClusters.has(cluster.id),
        })),
      }));
    }
  }, [expandedClusters, clusteredData.clusters.length]);

  // Recalculate cluster positions on scroll/resize
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handlePositionUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setClusteredData((prev) => ({
          ...prev,
          clusters: prev.clusters.map((cluster) => {
            if (cluster.element && isElementValid(cluster.element)) {
              return {
                ...cluster,
                position: calculateClusterPosition(cluster.element),
              };
            }
            return cluster;
          }),
        }));
      }, 16); // ~60fps throttling
    };

    window.addEventListener('scroll', handlePositionUpdate, { passive: true });
    window.addEventListener('resize', handlePositionUpdate, { passive: true });

    return () => {
      window.removeEventListener('scroll', handlePositionUpdate);
      window.removeEventListener('resize', handlePositionUpdate);
      clearTimeout(timeoutId);
    };
  }, [isElementValid, calculateClusterPosition]);

  // Toggle cluster expansion
  const toggleCluster = useCallback((clusterId: string) => {
    setExpandedClusters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clusterId)) {
        newSet.delete(clusterId);
      } else {
        // Close other clusters when opening a new one
        newSet.clear();
        newSet.add(clusterId);

        // Mark cluster as animated if it's being opened for the first time
        setAnimatedClusters((prevAnimated) => {
          if (!prevAnimated.has(clusterId)) {
            return new Set(prevAnimated).add(clusterId);
          }
          return prevAnimated;
        });
      }
      return newSet;
    });
  }, []);

  // Check if cluster should show first-time animation
  const shouldAnimateCluster = useCallback(
    (clusterId: string) => {
      return (
        expandedClusters.has(clusterId) && !animatedClusters.has(clusterId)
      );
    },
    [expandedClusters, animatedClusters]
  );

  // Mark cluster animation as complete
  const markClusterAnimationComplete = useCallback((clusterId: string) => {
    setAnimatedClusters((prev) => new Set(prev).add(clusterId));
  }, []);

  // Collapse all clusters
  const collapseAllClusters = useCallback(() => {
    setExpandedClusters(new Set());
  }, []);

  // Get cluster by ID
  const getClusterById = useCallback(
    (clusterId: string) => {
      return clusteredData.clusters.find((cluster) => cluster.id === clusterId);
    },
    [clusteredData.clusters]
  );

  // Get tasks in a specific cluster
  const getClusterTasks = useCallback(
    (clusterId: string) => {
      const cluster = getClusterById(clusterId);
      return cluster?.tasks || [];
    },
    [getClusterById]
  );

  // Check if a task is in any cluster
  const isTaskInCluster = useCallback(
    (taskId: string) => {
      return clusteredData.clusters.some((cluster) =>
        cluster.tasks.some((task) => task.task_id === taskId)
      );
    },
    [clusteredData.clusters]
  );

  // Get cluster containing a specific task
  const getTaskCluster = useCallback(
    (taskId: string) => {
      return clusteredData.clusters.find((cluster) =>
        cluster.tasks.some((task) => task.task_id === taskId)
      );
    },
    [clusteredData.clusters]
  );

  // Clean up invalid clusters (where element is no longer in DOM)
  const cleanupInvalidClusters = useCallback(() => {
    setClusteredData((prev) => {
      const validClusters = prev.clusters.filter((cluster) =>
        isElementValid(cluster.element)
      );

      // Add tasks from invalid clusters back to individual tasks
      const invalidClusterTasks = prev.clusters
        .filter((cluster) => !isElementValid(cluster.element))
        .flatMap((cluster) => cluster.tasks);

      return {
        clusters: validClusters,
        individualTasks: [...prev.individualTasks, ...invalidClusterTasks],
      };
    });
  }, [isElementValid]);

  // Periodically clean up invalid clusters
  useEffect(() => {
    const interval = setInterval(cleanupInvalidClusters, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [cleanupInvalidClusters]);

  return {
    clusteredData,
    expandedClusters,
    animatedClusters,
    toggleCluster,
    collapseAllClusters,
    getClusterById,
    getClusterTasks,
    isTaskInCluster,
    getTaskCluster,
    cleanupInvalidClusters,
    shouldAnimateCluster,
    markClusterAnimationComplete,
    isProcessing,
  };
};
